/**
 * Estabelecimento Service - Epic 17 Story 17.2
 *
 * API client para gestão de Estabelecimentos (anteriormente Hospitais)
 * Endpoints: /api/estabelecimentos
 */

import apiClient from './apiClient';
import type { Estabelecimento, LocalContagem } from '../types/estabelecimento';

class EstabelecimentoService {

  // ==========================================
  // CRUD Estabelecimentos
  // ==========================================

  async listEstabelecimentos(): Promise<Estabelecimento[]> {
    const response = await apiClient.get('/estabelecimentos');
    return response.data;
  }

  async getEstabelecimento(id: number): Promise<Estabelecimento> {
    const response = await apiClient.get(`/estabelecimentos/${id}`);
    return response.data;
  }

  async createEstabelecimento(data: Partial<Estabelecimento>): Promise<Estabelecimento> {
    const response = await apiClient.post('/estabelecimentos', data);
    return response.data;
  }

  async updateEstabelecimento(id: number, data: Partial<Estabelecimento>): Promise<Estabelecimento> {
    const response = await apiClient.put(`/estabelecimentos/${id}`, data);
    return response.data;
  }

  async deleteEstabelecimento(id: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/estabelecimentos/${id}`);
    return response.data;
  }

  // ==========================================
  // Locais de Contagem
  // ==========================================

  async listLocaisContagem(estabelecimentoId: number, clienteId?: number): Promise<LocalContagem[]> {
    let url = `/estabelecimentos/${estabelecimentoId}/locais-contagem`;
    if (clienteId) {
      url += `?cliente_id=${clienteId}`;
    }
    const response = await apiClient.get(url);
    return response.data;
  }

  async createLocalContagem(
    estabelecimentoId: number,
    data: Partial<LocalContagem>
  ): Promise<LocalContagem> {
    const response = await apiClient.post(
      `/estabelecimentos/${estabelecimentoId}/locais-contagem`,
      data
    );
    return response.data;
  }

  async updateLocalContagem(
    estabelecimentoId: number,
    localId: number,
    data: Partial<LocalContagem>
  ): Promise<LocalContagem> {
    const response = await apiClient.put(
      `/estabelecimentos/${estabelecimentoId}/locais-contagem/${localId}`,
      data
    );
    return response.data;
  }

  async deleteLocalContagem(estabelecimentoId: number, localId: number): Promise<void> {
    await apiClient.delete(
      `/estabelecimentos/${estabelecimentoId}/locais-contagem/${localId}`
    );
  }

  // ==========================================
  // Export Excel
  // ==========================================

  async exportExcel(ativo?: boolean): Promise<void> {
    const params = ativo !== undefined ? `?ativo=${ativo}` : '';
    const response = await apiClient.get(`/hospitais/export-excel${params}`, {
      responseType: 'blob',
    });

    // Download do arquivo
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.setAttribute('download', `hospitais_export_${timestamp}.xlsx`);

    document.body.appendChild(link);
    link.click();
    link.remove();

    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  }

  // ==========================================
  // Formatters (helpers)
  // ==========================================

  formatCNPJ(cnpj: string): string {
    if (!cnpj) return '';
    // Remove tudo que não é dígito
    const digits = cnpj.replace(/\D/g, '');

    // CPF: 000.000.000-00
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    // CNPJ: 00.000.000/0000-00
    if (digits.length === 14) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return cnpj;
  }

  formatTelefone(telefone: string): string {
    if (!telefone) return '';
    const digits = telefone.replace(/\D/g, '');

    // Celular: (00) 00000-0000
    if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    // Fixo: (00) 0000-0000
    if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    return telefone;
  }

  formatCEP(cep: string): string {
    if (!cep) return '';
    const digits = cep.replace(/\D/g, '');
    if (digits.length === 8) {
      return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
    }
    return cep;
  }
}

export default new EstabelecimentoService();
export { EstabelecimentoService };
export type { Estabelecimento, LocalContagem };
